import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doGetTips, setTips } from '@app/store/slices/dataSlice';
import { notificationController } from '@app/controllers/notificationController';
import { Tip } from './types';
import { ColumnsType } from 'antd/es/table';
import { Tag, Popconfirm, Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button } from '@app/components/common/buttons/Button/Button';
import { Table } from '@app/components/common/Table/Table';
import { PageTitle, Toolbar, ToolbarActions } from '@app/components/common/PageToolbar/PageToolbar';
import { Card as CommonCard } from '@app/components/common/Card/Card';
import { deleteTip } from '@app/api/admin.api';

const brandColor = (brand: string) => (brand?.toLowerCase() === 'wavon' ? 'orange' : 'green');

const TipsPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const tips = useAppSelector((state) => state.data.tips);
    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
        fetchTips();
    }, []);

    const fetchTips = () => {
        setLoading(true);
        dispatch(doGetTips())
            .unwrap()
            .then((res) => {
                dispatch(setTips(res));
                setLoading(false);
            })
            .catch((err) => {
                notificationController.error({ message: err.message });
                setLoading(false);
            });
    };

    const columns: ColumnsType<Tip> = [
        {
            title: 'Brand',
            dataIndex: 'brand',
            width: '10%',
            render: (brand: string) => <Tag color={brandColor(brand)}>{(brand || '').toUpperCase()}</Tag>,
        },
        {
            title: 'Tip',
            dataIndex: 'text',
        },
        {
            title: 'Order',
            dataIndex: 'order',
            width: '8%',
        },
        {
            title: 'Action',
            dataIndex: 'actions',
            width: '15%',
            render: (text: string, record: Tip) => {
                return (
                    <Space>
                        <Button
                            size="small"
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => navigate('edit', { state: { record } })}
                        />
                        <Popconfirm
                            title={'Are you sure you want to delete this tip?'}
                            onConfirm={() => {
                                deleteTip(record.id)
                                    .then(() => {
                                        notificationController.success({ message: 'Tip deleted successfully.' });
                                        fetchTips();
                                    })
                                    .catch((error) => {
                                        notificationController.error({ message: error.message || error });
                                    });
                            }}
                            okText="Yes"
                            cancelText="No">
                            <Button danger size="small" type="primary" icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <Toolbar>
                <PageTitle>Daily Tips</PageTitle>
                <ToolbarActions>
                    <Button
                        type="primary"
                        onClick={() => {
                            navigate('create');
                        }}
                    >
                        {'Create Tip'}
                    </Button>
                </ToolbarActions>
            </Toolbar>
            <CommonCard id="basic-table" padding="1.25rem 1.25rem 0">
                <Table columns={columns} dataSource={tips} loading={isLoading} rowKey="id" scroll={{ x: 800 }} />
            </CommonCard>
        </>
    );
};

export default TipsPage;
